import { importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { TitleStrategy } from '@angular/router';

import { IdxTitleStrategy } from 'src/app/services/idx-title-strategy.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { CustomErrorMessagesService } from './app/services/custom-error-messages.service';
import { GET_ERROR_MESSAGE_FN_TOKEN } from '@bioreferencehealth/ui-kit';
import { DATE_PICKER_MIN_DATE } from '@bioreferencehealth/ui-kit';
import { AppInitializerService } from 'src/app/services/app-initializer.service';
import { ChunkLoadErrorHandlerService } from 'src/app/services/chunk-load-error-handler.service';
import { DebugConsoleService } from 'src/app/services/debug-console.service';
import { FOOTER_LINKS } from './shared/tokens/footer-links.token';
import { footerLinks } from './app/consts/footer-links.const';
import { FOOTER_SERVICE } from './shared/tokens/footer-service.token';
import { FooterService } from './shared/components/footer/footer.service';
import { CustomerSupportApiService, DefaultLabApiService } from 'src/app/services';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { requestHeaderInterceptorFn } from 'src/interceptors/request-header/request-header-interceptor.fn';
import { generalErrorInterceptorFn } from 'src/interceptors/error/general-error-interceptor.fn';
import { NgxsModule } from '@ngxs/store';
import { AppState } from 'src/states';
import { AppInfoState } from './app/states/app-info.state';
import { CustomerSupportState } from './app/states/customer-support.state';
import { DefaultLabState } from './app/states/default-lab.state';
import { ModalState } from './app/states/modal.state';
import { NavigationState } from './app/states/navigation.state';
import { SlidePanelState } from './app/states/slide-panel.state';
import { GlobalSettingsState } from 'src/states/global-settings/global-settings.state';
import { PostMessageState } from 'src/app/states/post-message/post-message.state';
import { SidebarPreferencesState } from 'src/app/components/side-bar/sidebar-preferences.state';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { SecurityModule } from 'src/modules/security/security.module';
import { addDays, startOfDay, subYears } from 'date-fns';
import { MAX_PATIENT_AGE } from './shared/constants/max-patient-age.const';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppRoutingModule } from './app/app-routing.module';
import { SharedModule } from './shared/shared.module';
import { KeepAliveModule } from './modules/keep-alive/keep-alive.module';
import { DocumentsModule } from 'src/modules/documents/documents.module';
import { FormsModule } from '@angular/forms';
import { AppComponent } from './app/app.component';
import en from '@angular/common/locales/en';
import { registerLocaleData } from '@angular/common';
import { AnalyticsInitializerService } from 'src/modules/analytics/services/analytics-initializer.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { environment } from 'src/environments/environment';
import { provideChatApiConfig } from './app/pages/chat/providers/chat-api-config.provider';

registerLocaleData(en);

// The Redux DevTools plugin is dev-only tooling. Keeping it out of the module list for
// production builds lets Angular/esbuild dead-code-eliminate `@ngxs/devtools-plugin` entirely,
// and avoids exposing the full NGXS state tree to the browser DevTools extension in production.
const ngxsDevtoolsImports = !environment.production ? [NgxsReduxDevtoolsPluginModule.forRoot()] : [];

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      NgxsModule.forRoot([
        AppState,
        AppInfoState,
        CustomerSupportState,
        DefaultLabState,
        ModalState,
        NavigationState,
        SlidePanelState,
        GlobalSettingsState,
        PostMessageState,
        SidebarPreferencesState
      ]),
      NgxsFormPluginModule.forRoot(),
      NgxsRouterPluginModule.forRoot(),
      ...ngxsDevtoolsImports,
      SecurityModule,
      AppRoutingModule,
      SharedModule,
      KeepAliveModule,
      DocumentsModule,
      FormsModule
    ),
    // Loaded asynchronously (separate chunk, fetched after first render) instead of
    // BrowserAnimationsModule so the ~58 KB @angular/animations browser runtime is no longer
    // part of the critical initial bundle.
    provideAnimationsAsync(),
    provideZoneChangeDetection(),
    // Must be registered here (root), not at route/component level: `@bioreferencehealth/chat-lib`'s
    // ChatService is `providedIn: 'root'`, so it always resolves CHAT_API_CONFIG through the root
    // environment injector when first constructed — route/component-level overrides of the token are
    // never visible to it, regardless of navigation order. Registering it only at route/component
    // level (as a prior bundle-size optimization attempted) silently falls back to the library's own
    // default endpoints. This does mean `@bioreferencehealth/chat-lib`/`chart.js` load eagerly again.
    //
    // TODO(revert-when-chat-lib-fixed): this re-introduces `@bioreferencehealth/chat-lib`/`chart.js`
    // (~270 KB) into the eager initial bundle. This was reported upstream (see chat-lib issue/PR
    // discussing CHAT_API_CONFIG root-injector resolution + a chat-lib/config secondary entry point).
    // Once a chat-lib release ships EITHER of these fixes:
    //   (a) ChatService is no longer `providedIn: 'root'` (host apps provide it explicitly), OR
    //   (b) CHAT_API_CONFIG moves to a lightweight secondary entry point (e.g.
    //       `@bioreferencehealth/chat-lib/config`) that does not import `chart.js`,
    // revert this workaround:
    //   1. Bump `@bioreferencehealth/chat-lib` to the fixed version.
    //   2. Remove `provideChatApiConfig()` and its import from this file (main.ts).
    //   3. Re-add `providers: [provideChatApiConfig()]` to the route object in
    //      `src/app/pages/chat/chat.routes.ts`.
    //   4. Re-add `providers: [provideChatApiConfig()]` to the `@Component` decorator of
    //      `PatientChatSlidePanelContainerComponent`
    //      (src/app/pages/patients/pages/patient-details/components/patient-chat-slide-panel-container/patient-chat-slide-panel-container.component.ts).
    //   5. If fix (b) was shipped, update the import in `chat-api-config.provider.ts` to pull
    //      `CHAT_API_CONFIG`/`ChatApiConfig` from the new lightweight entry point instead of the
    //      main `@bioreferencehealth/chat-lib` package, and update its comment accordingly.
    //   6. Run `ng build --configuration production --stats-json` and confirm the chat-lib/chart.js
    //      chunk is no longer statically imported by main.js (only by the two lazy chunks above) and
    //      that the initial bundle size drops back down (~1.87 MB at the time this was written).
    //   7. Re-run the chat + patient-chat-slide-panel unit tests and manually verify chat still works
    //      correctly on both surfaces before removing this TODO block.
    provideChatApiConfig(),
    NavigationService,
    CustomErrorMessagesService,
    {
      provide: TitleStrategy,
      useClass: IdxTitleStrategy
    },
    {
      provide: GET_ERROR_MESSAGE_FN_TOKEN,
      useFactory: (service: CustomErrorMessagesService): unknown => {
        return service.getErrorMessage.bind(service);
      },
      deps: [CustomErrorMessagesService]
    },
    provideAppInitializer(() => inject(AppInitializerService).init()),
    provideAppInitializer(() => inject(AnalyticsInitializerService).init()),
    provideAppInitializer(() => {
      inject(ChunkLoadErrorHandlerService);
    }),
    // Real helpers only in local/local-development/local-qa builds; every other configuration
    // resolves DebugConsoleService to the no-op stub via fileReplacements (see angular.json).
    provideAppInitializer(() => {
      inject(DebugConsoleService).init();
    }),
    {
      provide: FOOTER_LINKS,
      useValue: footerLinks
    },
    {
      provide: FOOTER_SERVICE,
      useClass: FooterService
    },
    DefaultLabApiService,
    CustomerSupportApiService,
    { provide: NZ_I18N, useValue: en_US },
    { provide: DATE_PICKER_MIN_DATE, useValue: addDays(startOfDay(subYears(new Date(), MAX_PATIENT_AGE)), 1) },
    provideHttpClient(withInterceptors([requestHeaderInterceptorFn, generalErrorInterceptorFn]))
  ]
}).catch(err => console.error(err));
